import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import {
  Ban,
  CheckCircle,
  CopyIcon,
  EditIcon,
  EyeIcon,
  TrashIcon,
  XCircle,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { JDListResult } from "@/modules/jd/domain/types";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  useArchiveJD,
  useDeleteJD,
  useDuplicateJD,
  usePublishJD,
  useUnpublishJD,
} from "../api/queries";

type JDRow = JDListResult["data"][number];

type JDTableProps = {
  data: JDRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (jd: JDRow) => void;
  onView: (jd: JDRow) => void;
};

const statusLabels: Record<string, string> = {
  draft: "ຮ່າງ",
  published: "ໃຊ້ງານ",
  archived: "ບໍ່ໃຊ້ງານ",
};

const statusVariants: Record<string, "default" | "secondary" | "destructive"> =
  {
    draft: "secondary",
    published: "default",
    archived: "destructive",
  };

export function JDTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
}: JDTableProps) {
  const { has } = usePermissions();
  const archive = useArchiveJD();
  const duplicate = useDuplicateJD();
  const deleteJD = useDeleteJD();
  const publish = usePublishJD();
  const unpublish = useUnpublishJD();

  const columns: TanstackReactTable.ColumnDef<JDRow>[] = [
    createSortableColumn<JDRow>("title", "ຊື່ຕໍາແໜ່ງ", { size: 200 }),
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 120,
    },
    {
      id: "level",
      header: "ລະດັບ",
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {row.original.level}
        </Badge>
      ),
      size: 100,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={statusVariants[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<JDRow>("updatedAt", "ວັນທີອັບເດດ", {
      size: 120,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-muted-foreground text-xs">
          {row.original.updatedAt
            ? formatDateTime(row.original.updatedAt)
            : "-"}
        </div>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: JDRow } }) => {
        const id = row.original.id as string;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("jd:read"))
          actions.push({
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          });

        if (has("jd:update"))
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

        if (has("jd:create"))
          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: async () => {
              const departmentId = row.original.departmentId;
              toast.promise(
                duplicate.run({
                  id,
                  title: `${row.original.title} (Copy)`,
                  departmentId,
                }),
                {
                  loading: "ກໍາລັງຄັດລອກ JD...",
                  success: "ຄັດລອກ JD ສໍາເລັດ",
                  error: "ຄັດລອກ JD ລົ້ມເຫຼວ",
                },
              );
            },
          });

        if (has("jd:update") && row.original.status !== "published")
          actions.push({
            label: "ເປີດໃຊ້ງານ",
            icon: <CheckCircle className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ເປີດໃຊ້ງານ JD",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດໃຊ້ງານ JD ນີ້?",
                actionText: "ເປີດໃຊ້ງານ",
              });
              if (ok)
                toast.promise(publish.run(id), {
                  loading: "ກໍາລັງເປີດໃຊ້ງານ...",
                  success: "ເປີດໃຊ້ງານ JD ສໍາເລັດ",
                  error: "ເປີດໃຊ້ງານ JD ລົ້ມເຫຼວ",
                });
            },
          });

        if (has("jd:update") && row.original.status === "published")
          actions.push({
            label: "ປ່ຽນເປັນແບບຮ່າງ",
            icon: <XCircle className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ປ່ຽນເປັນແບບຮ່າງ JD",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປ່ຽນເປັນແບບຮ່າງ JD ນີ້?",
                actionText: "ຍົກເລີກ",
              });
              if (ok)
                toast.promise(unpublish.run(id), {
                  loading: "ກໍາລັງຍົກເລີກການເປີດໃຊ້ງານ...",
                  success: "ຍົກເລີກການເປີດໃຊ້ງານ JD ສໍາເລັດ",
                  error: "ຍົກເລີກການເປີດໃຊ້ງານ JD ລົ້ມເຫຼວ",
                });
            },
          });

        if (has("jd:update") && row.original.status === "published")
          actions.push({
            label: "ເກັບໄວ້",
            variant: "destructive",
            icon: <Ban className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ເກັບໄວ້ JD",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານ JD ນີ້?",
                actionText: "ເກັບໄວ້",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(archive.run(id), {
                  loading: "ກໍາລັງເກັບໄວ້...",
                  success: "ປິດການໃຊ້ງານ JD ສໍາເລັດ",
                  error: "ປິດການໃຊ້ງານ JD ລົ້ມເຫຼວ",
                });
            },
          });

        if (has("jd:delete") && row.original.status === "archived")
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບ JD",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ JD ນີ້? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(deleteJD.run(id), {
                  loading: "ກໍາລັງລຶບ JD...",
                  success: "ລຶບ JD ສໍາເລັດ",
                  error: (err: Error) => err.message || "ລຶບ JD ລົ້ມເຫຼວ",
                });
            },
          });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<JDRow, unknown>
      noDataMessage="ບໍ່ພົບ JD"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
